/**
 * parseEntityTransparency — decode DXF group 440 (entity transparency) into a
 * PDF stroke/fill alpha in `[0, 1]` (PDF-6.4).
 *
 * # DXF 440 packed integer (Autodesk / ezdxf reference)
 *
 * Group code 440 carries a 32-bit packed integer whose high byte is the
 * transparency TYPE and whose low byte carries an alpha value 0..255:
 *
 *   - `(value >> 24) & 0xFF === 0x00` → "ByLayer" sentinel (rarely seen; the
 *     model normally stores the string `"ByLayer"` instead). Caller falls back
 *     to the layer's own transparency (out of scope here) or fully opaque.
 *   - `(value >> 24) & 0xFF === 0x01` → "ByBlock" — resolved from the enclosing
 *     INSERT's transparency; without that chain we return `undefined` and the
 *     caller treats it as opaque (no override).
 *   - `(value >> 24) & 0xFF === 0x02` → "by entity" / "by object" — the low
 *     byte is the alpha (255 = fully opaque, 0 = fully transparent), matching
 *     ezdxf's `tools.transparency2float` interpretation.
 *
 * The drawing-model schema stores `transparency` as a STRING on
 * {@link drawingEntityBaseSchema} (defaults to `"0"`). The string may be:
 *   - `"0"` / `0` → fully opaque, no override (returns `1.0`).
 *   - `"ByLayer"` / `"ByBlock"` → caller falls back (we return `undefined`).
 *   - a stringified packed integer (e.g. `"33554432"`, `"33554560"`).
 *   - any unparseable garbage → `undefined` (no override; opaque).
 *
 * # Composition with CTB screening (PDF-6.3b)
 *
 * The caller multiplies this alpha by the CTB-derived screen alpha (also 0..1):
 *
 *   `composite = screenAlpha * entityAlpha`
 *
 * Both factors are independent opacity multipliers in PDF's compositing model,
 * so MULTIPLY is the correct combination (`/CA` and `/ca` cascade through
 * `q…Q` blocks; PDF 32000-1:2008 §11.3.4 "Source shape and opacity").
 *
 * # Storage contract — NO percentage-string path (Fix D, Codex #207)
 *
 * This decoder ONLY accepts the AutoCAD-internal DXF-440 packed form (the
 * `0x02……` integer from import) or the `"0"` / `"ByLayer"` / `"ByBlock"`
 * sentinels — exactly how the model stores entity transparency today (set by
 * the DXF importer; there is NO authoring path in the repo that writes a bare
 * percentage string like `"50"`). A bare `"50"` is therefore treated as an
 * (invalid) packed integer, NOT as "50% transparent": guessing a percent here
 * would MISREAD a real packed value (`50` has high byte `0x00`, i.e. not the
 * `0x02` by-entity type → `undefined` / opaque), so the decoder deliberately
 * does NOT add speculative percent decoding.
 *
 * If a FUTURE property-grid transparency editor is added, its 0–90 % UI value
 * MUST be converted to the packed form BEFORE it is written to the model —
 * matching how AutoCAD stores transparency internally — so this decoder stays
 * correct:
 *
 *   packed = 0x02000000 | Math.round((1 - pct / 100) * 255)
 *
 * (`pct` is the AutoCAD "Transparency" percent ∈ [0, 90]; `0x02000000` is the
 * by-entity type flag; the low byte is the 0..255 alpha where 255 = opaque.)
 * The editor — not this decoder — owns the percent→packed conversion.
 *
 * @returns alpha in `[0, 1]` (1 = fully opaque) or `undefined` when no override
 *          applies (ByLayer / ByBlock sentinels, unparseable input).
 */
export function parseEntityTransparency(
  raw: string | number | undefined | null,
): number | undefined {
  if (raw === undefined || raw === null) return undefined;

  // STRING sentinel forms ("ByLayer" / "ByBlock") — caller falls back, no
  // override applies (the AutoCAD-parity semantics are "inherit from the
  // referenced context"; we treat that as no per-entity override here).
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed === "") return undefined;
    const upper = trimmed.toUpperCase();
    if (upper === "BYLAYER" || upper === "BYBLOCK") return undefined;
    // Fall through to numeric parse below.
  }

  // Numeric parse — the packed integer (or plain `0`).
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return undefined;
  // Reject NON-integer values: DXF 440 is always an integer code; a float
  // here is a model bug, not a partial-opaque encoding. Treat as no override
  // rather than guessing.
  if (!Number.isInteger(n)) return undefined;

  // Plain `0` (the model default) → fully opaque, NO ExtGState wrapper needed
  // downstream. We return 1.0 so the caller's multiply branch is a no-op
  // (`screen * 1 === screen`) without a special-case at the call site.
  if (n === 0) return 1.0;

  // Negative values are not a valid DXF 440 encoding.
  if (n < 0) return undefined;

  // High byte = transparency type. We accept only the "by entity" form (0x02);
  // a ByBlock (0x01) marker returns undefined so the caller treats it as no
  // override (blocks are out of scope for PDF-6.4; ByBlock without a parent
  // INSERT chain is fully opaque from the entity's perspective).
  const typeFlag = (n >>> 24) & 0xff;
  if (typeFlag === 0x01) return undefined;
  // 0x00 high byte without a value (`n === 0`) is handled above. Any other
  // non-0x02 type flag is an unknown encoding — return undefined (no override).
  if (typeFlag !== 0x02) return undefined;

  // Low byte = alpha (0..255 where 255 = opaque, 0 = transparent per ezdxf
  // tools.transparency2float). Map to [0, 1].
  const lowByte = n & 0xff;
  return lowByte / 255;
}
